import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Typography,
  Card, Chip,Grid
} from '@mui/material';


import { useDispatch, useSelector } from '../../../../redux/store';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import InvoiceApprovalModal from './InvoiceApprovalModal';
import { HOST_API_URL } from '../../../../config-global';
import {
  getInvoiceDataFromRedux,
  getFacilityInvoicingApprovers,
  saveFacilityApprovers,
  getApproverOptions,
  deleteFacilityApprovers
} from '../../../../redux/slices/invoicing';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton'
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';

InvoiceApproval.propTypes = {
  isManual: PropTypes.bool,
  gridSx: PropTypes.object,
  heightSx: PropTypes.object,
};
export default function InvoiceApproval({ isManual, gridSx, heightSx }) {
  const dispatch = useDispatch();

  const downloadFacilityInvoiceApprovers = () => window.open(`${HOST_API_URL}Api/Invoices/DownloadFacilityInvoiceApprovers?isManual=${isManual}`, '_blank');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setIsLoading(true);
    dispatch(getFacilityInvoicingApprovers(isManual)).finally(() => setIsLoading(false));
    dispatch(getInvoiceDataFromRedux(isManual));
    dispatch(getApproverOptions(isManual));
  }, [dispatch, isManual]);

  const {
    data: {
      invoiceApproverOptions = [],
      invoiceData,
      facilityInvoicingApprovers,
    },
  } = useSelector((state) => state.invoicing);

  const {
    facilities = [],
    vendors = [],
    manualVendors = [],
    manualFacilities = []
  } = invoiceData;
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleOpenAdd = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async (form) => {
    const response = await dispatch(
      saveFacilityApprovers({
        ...form,
        isManual,
      })
    );
    if (response === "success") {
      enqueueSnackbar('Approvers saved successfully', {
        variant: 'success',
      });
      dispatch(getFacilityInvoicingApprovers(isManual));
      handleClose();
    } else {
      enqueueSnackbar('Error saving approvers', {
        variant: 'error',
      });
    }
  };
  const handleDelete = async (form) => {
    const response = await dispatch(
      deleteFacilityApprovers({
        ...form,
        isManual,
      })
    );
    if (response === "success") {
      enqueueSnackbar('Approvers deleted successfully', {
        variant: 'success',
      });
      dispatch(getFacilityInvoicingApprovers(isManual));
    } else {
      enqueueSnackbar('Error deleting approvers', {
        variant: 'error',
      });
    }
  };

  const handleDeleteApprover = (row, approver) => {
    handleDelete({
      facilityIds: [row.facilityId],
      vendorIds: [row.vendorId],
      approvers: [{ id: approver.value }],
    });
  };

  const handleDeleteRow = (row) => {
    handleDelete({
      facilityIds: [row.facilityId],
      vendorIds: [row.vendorId],
      approvers: row.approvers.map(a => ({
        id: a.value,
      })),
    });
  };

  const columns = [
    {
      field: 'facilityName',
      headerName: 'Facility',
      flex: 1,
    },
    {
      field: 'vendorName',
      headerName: 'Vendor',
      flex: 1,
    },
    {
      field: 'approvers',
      headerName: 'Approvers',
      flex:3,
      minwidth: 150,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            py: 0.5,
          }}
        >
          {row.approvers?.map((approver, index) => (
            <Chip
              key={approver.value}
              size="small"
              label={`${index + 1}. ${approver.value}`}
              onDelete={() => handleDeleteApprover(row, approver)}
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          ml: 1,
          flexShrink: 0,
        }}>
          <ConfirmDialog
            icon="delete-outline"
            title="Delete"
            color="error"
            content={`Are you sure you want to delete facility vendor approvers?`}
            actionButton="Delete"
            action={() => handleDeleteRow(row)}
          />
        </Box>
      ),
    },
  ];

  const permissionRequired = isManual ? "FacilityApprovers-Edit" : "ElectronicFacilityApprovers-Edit";

  return (
    <Card sx={{ p: 2, ...heightSx }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" color="primary.main">
           Invoice Approvers
        </Typography>
        <Grid item>
          <Button
              sx={{ mr: 1 }}
            variant="contained"
            color="secondary"
            onClick={handleOpenAdd}
            size="small"
          >
            Edit Approvers
          </Button>
          <AwaitButton
            variant="outlined"
            sx={{ mr: 1 }}
            size="small"
            color="secondary"
            onClick={downloadFacilityInvoiceApprovers}
            startIcon={<Iconify icon="file-icons:microsoft-excel" />}
          >
            Download
        </AwaitButton>
      </Grid>
    </Box>
      <Grid item xs={12}>
        {isLoading && !facilityInvoicingApprovers?.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <LoadingAnimation />
          </Box>
        ) : (
          <CustomDataGrid
            isLoading={isLoading}
            gridId={`invoice-approvers-${isManual ? 'manual' : 'standard'}`}
            data={facilityInvoicingApprovers || []}
            gridColumns={columns}
            boxSX={{ width: '100%', height: 'calc(100vh - 220px)' }}
            sx={gridSx}
            getRowId={(row) => `${row.facilityId}-${row.vendorId}`}
          />
        )}
      </Grid>

        <InvoiceApprovalModal
          open={open}
          onClose={handleClose}
          onSave={handleSave}
          onDelete={handleDelete}
          vendors={isManual ? manualVendors : vendors}
          facilities={isManual ? manualFacilities : facilities}
          approvers={invoiceApproverOptions}
          permissionType={permissionRequired}
        />
    </Card>
  );
}